// components/animal/AnimalFamilyTree.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Avatar,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { getAnimalById } from "../../api/animal"; // <-- NEW: use a paged/lite list endpoint
import FamilyTree from "./FamilyTree";
import { animalApi } from "../../api/animal";

const AnimalFamilyTree = ({ animalId, farmTenantId = null }) => {
  const profileQs =
    farmTenantId != null && Number.isFinite(farmTenantId)
      ? `?tenant_id=${farmTenantId}`
      : "";
  const [parents, setParents] = useState({ dam: null, sire: null });
  const [descendants, setDescendants] = useState([]); // <-- renamed from offspring
  const [currentAnimal, setCurrentAnimal] = useState(null);
  const [allAnimals, setAllAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1) Load current animal + parents
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const cur = await getAnimalById(animalId, farmTenantId);
        if (abort) return;
        setCurrentAnimal(cur);

        const [dam, sire] = await Promise.all([
          cur?.dam_id ? getAnimalById(cur.dam_id, farmTenantId) : Promise.resolve(null),
          cur?.sire_id ? getAnimalById(cur.sire_id, farmTenantId) : Promise.resolve(null),
        ]);
        if (abort) return;

        setParents({ dam, sire });
      } catch (err) {
        if (!abort) setError(err?.message || "Failed to load family data");
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, [animalId, farmTenantId]);

  // 2) Load ALL animals (paged) to compute descendants
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        // Must stay within API cap: GET /animal/get_animals uses per_page le=100
        const pageSize = 100;
        let page = 1;
        const acc = [];
        while (true) {
          const res = await animalApi.listAnimals({
            page,
            pageSize,
            tenantId: farmTenantId ?? undefined,
          });
          if (abort) return;
          const items = res?.items || [];
          acc.push(...items);
          if (items.length < pageSize) break;
          if (typeof res?.total === "number" && acc.length >= res.total) break;
          page += 1;
        }
        setAllAnimals(acc);
      } catch (err) {
        if (!abort) setError(err?.message || "Failed to load animals list");
      }
    })();

    return () => {
      abort = true;
    };
  }, [farmTenantId]);

  // 3) Compute descendants (all generations) from allAnimals
  const computedDescendants = useMemo(() => {
    if (!currentAnimal?.id || allAnimals.length === 0) return [];

    // Build index: parentId -> children[]
    const childrenByParent = new Map();
    for (const a of allAnimals) {
      if (!a) continue;
      const links = [a?.dam_id, a?.sire_id].filter(Boolean);
      for (const pid of links) {
        if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
        childrenByParent.get(pid).push(a);
      }
    }

    // DFS from currentAnimal.id to collect all descendants
    const seen = new Set();
    const out = [];
    const stack = [currentAnimal.id];

    while (stack.length) {
      const pid = stack.pop();
      const kids = childrenByParent.get(pid) || [];
      for (const child of kids) {
        if (seen.has(child.id)) continue;
        seen.add(child.id);
        out.push(child);
        stack.push(child.id);
      }
    }

    // Optional: stable sort (e.g., by DOB ascending)
    out.sort((a, b) => (a?.dob || "").localeCompare(b?.dob || ""));
    return out;
  }, [currentAnimal?.id, allAnimals]);

  // keep state in sync for the tree + below section
  useEffect(() => {
    setDescendants(computedDescendants);
  }, [computedDescendants]);

  if (loading && !currentAnimal) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!currentAnimal) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Animal not found.
      </Alert>
    );
  }

  // helper for chip color
  const statusToColor = (statusRaw) => {
    const s = (statusRaw || "").toString().toLowerCase();
    if (s === "on_farm" || s === "alive" || s === "active") return "success";
    if (s === "sold") return "warning";
    if (s === "deceased") return "error";
    if (s === "transferred") return "info";
    return "default";
  };

  return (
    <Box>
      {/* Family tree visualization */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Lineage
        </Typography>
        <FamilyTree
          parents={parents}
          currentAnimal={currentAnimal}
          offspring={descendants} // <- now all descendants
        />
      </Box>

      {/* Parents */}
      <Typography variant="h6" gutterBottom>
        Parents
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Dam (Mother)
            </Typography>
            {parents.dam ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Avatar sx={{ mr: 2 }}>
                    {parents.dam.alias?.charAt(0) ||
                      parents.dam.tag_number?.charAt(0) ||
                      "D"}
                  </Avatar>
                  <Box>
                    <Typography>
                      {parents.dam.alias || "Unnamed"} ({parents.dam.tag_number}
                      )
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {parents.dam.breed || "—"}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  component={Link}
                  to={`/animal/${parents.dam.id}${profileQs}`}
                  variant="outlined"
                  size="small"
                >
                  View Profile
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No dam information available
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sire (Father)
            </Typography>
            {parents.sire ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Avatar sx={{ mr: 2 }}>
                    {parents.sire.alias?.charAt(0) ||
                      parents.sire.tag_number?.charAt(0) ||
                      "S"}
                  </Avatar>
                  <Box>
                    <Typography>
                      {parents.sire.alias || "Unnamed"} (
                      {parents.sire.tag_number})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {parents.sire.breed || "—"}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  component={Link}
                  to={`/animal/${parents.sire.id}${profileQs}`}
                  variant="outlined"
                  size="small"
                >
                  View Profile
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No sire information available
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Descendants (formerly Offspring) */}
      <Typography variant="h6" gutterBottom>
        Offspring
      </Typography>
      {descendants.length > 0 ? (
        <Grid container spacing={2}>
          {descendants.map((child) => (
            <Grid item xs={12} sm={6} md={4} key={child.id}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Avatar sx={{ mr: 2 }}>
                    {child.alias?.charAt(0) ||
                      child.tag_number?.charAt(0) ||
                      "A"}
                  </Avatar>
                  <Box>
                    <Typography>
                      {child.alias || "Unnamed"} ({child.tag_number})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(child.breed || "—") + " • " + (child.gender || "—")}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Born:{" "}
                      {child?.dob
                        ? new Date(child.dob).toLocaleDateString()
                        : "—"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={child.status || "—"}
                    size="small"
                    color={statusToColor(child.status)}
                  />
                  {child.birth_weight ? (
                    <Chip label={`${child.birth_weight} kg`} size="small" />
                  ) : null}
                </Box>

                <Button
                  component={Link}
                  to={`/animal/${child.id}${profileQs}`}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  View Profile
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No offspring recorded
        </Typography>
      )}
    </Box>
  );
};

export default AnimalFamilyTree;
